import {Injectable} from '@angular/core';

import { Observable, of} from 'rxjs';

import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import Geometry from '@arcgis/core/geometry/Geometry';
import Extent from '@arcgis/core/geometry/Extent';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';

import LinearUnits = __esri.LinearUnits;

const METER = 9001;
const KILOMETER = 9036;

@Injectable({
  providedIn: 'root'
})
export class MapService {
  protected _id = 1;

  nextId() {
    return this._id++;
  }

  jsonToPolyline(json: any, toGeographic: boolean): Observable<Polyline> {
    const obj = this.jsonParse(json);
    let geom: Polyline = Polyline.fromJSON(obj);
    if (toGeographic && geom.spatialReference.isWGS84) {
      geom = webMercatorUtils.webMercatorToGeographic(geom) as Polyline;
    }
    return of(geom);
  }

  /**
   * Obtener el largo de una polilinea
   * @param geometry Polilina a calcular la distancia
   * @param unit Unidad del resultado
   */
  geodesicLenght(geometry: Geometry, unit: LinearUnits = 'meters'): Observable<number> {
    return of(geometryEngine.geodesicLength(geometry, unit));
  }

  geodesicArea(geometry: Polygon, unit: number): Observable<number> {
    return of(geometryEngine.geodesicArea(geometry, unit));
  }

  geodesicBuffer(geometry: Geometry, distance: number, unit: number): Observable<Polygon | Polygon[]> {
    return of(geometryEngine.geodesicBuffer(geometry, distance, unit));
  }

  unionExtents(extents: Extent[]): Extent {
    if (!extents || !extents[0]) {
      return null;
    }
    return extents.reduce((e, c) => e.union(c.extent), extents[0].clone());
  }

  // json
  jsonParse(json: string | any): any {
    let obj = json;
    if (json && typeof json === 'string') {
      obj = JSON.parse(json.replace(/'/g, '"'));
    }
    return obj;
  }

  jsonToPolygon(json: any, toGeographic: boolean): Observable<Polygon> {
    const obj = this.jsonParse(json);
    let geom: Polygon = Polygon.fromJSON(obj);
    if (toGeographic && !geom.spatialReference.isWGS84) {
      geom = webMercatorUtils.webMercatorToGeographic(geom) as Polygon;
    }
    return of(geom);
  }



  /**
   * Creates a new geometry through intersection between two geometries.
   */
  intersect(geometry: Geometry | Geometry[], intersector: Geometry): Geometry | Geometry[] {
    return geometryEngine.intersect(geometry, intersector);
  }

  /**
   *   Indicates if one geometry intersects another geometry.
   */
  intersects(geometry1: Geometry, geometry2: Geometry): boolean {
    return geometryEngine.intersects(geometry1, geometry2);
  }

  /**
   * Creates the difference of two geometries.
   *
   * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-geometryEngine.html#difference)
   */
  difference(inputGeometry: Geometry | Geometry[], subtractor: Geometry): Geometry | Geometry[] {
    return geometryEngine.difference(inputGeometry, subtractor);

  }

  stringJsonToGeometry(json: string | object, toGeographic: boolean = false): Geometry {
    const obj = this.jsonParse(json);
    const geometry = this.jsonToGeometry(obj, toGeographic);
    return geometry;
  }

  jsonToGeometry(obj: Object | object, toGeographic: boolean = false): Geometry {
    let geometry: Geometry;
    if (obj) {
      if (obj.hasOwnProperty('rings')) {
        geometry = Polygon.fromJSON(obj);
      } else if (obj.hasOwnProperty('paths')) {
        geometry = Polyline.fromJSON(obj);
      } else {
        geometry = Point.fromJSON(obj);
      }
      if (toGeographic && !geometry.spatialReference.isWGS84) {
        geometry = webMercatorUtils.webMercatorToGeographic(geometry);
      }
    }
    return geometry;
  }

  itemToGraphic(item: any, graphicAttr: string, extraAttr = {}): Graphic {
    const json = item[graphicAttr];
    let graphic = null;
    if (json) {
      try{
        const geometry = this.stringJsonToGeometry(json, true);

        graphic = new Graphic({
          geometry,
          attributes: {
            ...item,
            graphicAttr,
            ...extraAttr
          }
        });
      }
      catch (e) {
        console.error('Error stringJsonToGeometry', e);
      }
    }
    return graphic;
  }

  /**
   * Esto es cuando es un array la geometria
   * @param item
   * @param graphicAttr
   * @returns
   */
  itemToGraphics(item: any, graphicAttr: string, extraAttr = {}): Graphic[] {
    let graphics = [];
    try{
      const json = item[graphicAttr];
      const jsonItems = this.jsonParse(json);
      if (jsonItems) {
        for (let jsonItem of jsonItems) {
          const geometry = this.jsonToGeometry(jsonItem, true);
          graphics.push(new Graphic({
            geometry,
            attributes: {
              ...item,
              graphicAttr,
              ...extraAttr
            }
          }));
        }
      }
    }catch (e) {
      console.log('error', e);
    }
    return graphics;
  }

  geometryLabelPoint(geometry: Geometry): Point {
    const buff = geometryEngine.geodesicBuffer(geometry, 100, METER, true) as Polygon;
    return buff.centroid;
  }

}
